/**
 * Fetches visits and summary data for two chronology versions and computes
 * the diff between them. Mirrors the SWR pattern used by `useCaseSummary`,
 * sharing its `/medchron/summary` cache key so a version already loaded by
 * the Summary tab doesn't get re-fetched here.
 */
import { useAPI } from "@/hooks/useAPI";
import { MedChronServiceV2 } from "@/services/medChronServiceV2";
import { SummaryServiceV2 } from "@/services/summaryServiceV2";
import type { MedVisit } from "@/types/medchron";
import type { MedSummaryData } from "@/types/summary";
import { useMemo } from "react";
import useSWR from "swr";

import { computeChronologyDiff } from "../diffChronology";
import type { ChronologyDiffResult } from "../types";

const CASE_SUMMARY_DATA_KEY = "/medchron/summary";
const CASE_VISITS_KEY = "/medchron/visits";

interface UseChronologyVersionsDiffParams {
  caseId: string;
  baseVersionId: string;
  compareVersionId: string;
}

interface UseChronologyVersionsDiffResult {
  diff: ChronologyDiffResult | null;
  isLoading: boolean;
  error: string | null;
}

export const useChronologyVersionsDiff = ({
  caseId,
  baseVersionId,
  compareVersionId,
}: UseChronologyVersionsDiffParams): UseChronologyVersionsDiffResult => {
  const apiV2 = useAPI("v2");
  const medChronServiceV2 = useMemo(() => new MedChronServiceV2(apiV2), [apiV2]);
  const summaryServiceV2 = useMemo(() => new SummaryServiceV2(apiV2), [apiV2]);

  const {
    data: baseVisits,
    error: baseVisitsError,
    isLoading: isBaseVisitsLoading,
  } = useSWR<MedVisit[]>(
    [CASE_VISITS_KEY, caseId, baseVersionId] as const,
    () => medChronServiceV2.getVisits(caseId, baseVersionId),
    { revalidateOnFocus: false }
  );

  const {
    data: compareVisits,
    error: compareVisitsError,
    isLoading: isCompareVisitsLoading,
  } = useSWR<MedVisit[]>(
    [CASE_VISITS_KEY, caseId, compareVersionId] as const,
    () => medChronServiceV2.getVisits(caseId, compareVersionId),
    { revalidateOnFocus: false }
  );

  const {
    data: baseSummary,
    error: baseSummaryError,
    isLoading: isBaseSummaryLoading,
  } = useSWR<MedSummaryData>(
    [CASE_SUMMARY_DATA_KEY, caseId, baseVersionId] as const,
    () => summaryServiceV2.getSummary(caseId, baseVersionId),
    { revalidateOnFocus: false }
  );

  const {
    data: compareSummary,
    error: compareSummaryError,
    isLoading: isCompareSummaryLoading,
  } = useSWR<MedSummaryData>(
    [CASE_SUMMARY_DATA_KEY, caseId, compareVersionId] as const,
    () => summaryServiceV2.getSummary(caseId, compareVersionId),
    { revalidateOnFocus: false }
  );

  const diff = useMemo(() => {
    if (!baseVisits || !compareVisits) return null;
    return computeChronologyDiff(
      baseVisits,
      compareVisits,
      baseSummary ?? null,
      compareSummary ?? null
    );
  }, [baseVisits, compareVisits, baseSummary, compareSummary]);

  const hasError = Boolean(
    baseVisitsError || compareVisitsError || baseSummaryError || compareSummaryError
  );

  return {
    diff,
    isLoading:
      isBaseVisitsLoading ||
      isCompareVisitsLoading ||
      isBaseSummaryLoading ||
      isCompareSummaryLoading,
    error: hasError ? "Failed to load version comparison." : null,
  };
};
